import { Injectable } from '@angular/core';
import {
    Dropbox,
    DropboxAuth,
    DropboxResponse,
    DropboxResponseError,
    files
} from 'dropbox';
import { DropboxStore } from './dropbox.store';
import { DropboxResource, DropboxState } from './dropbox.model';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    pluck,
    repeat,
    scan,
    share,
    startWith,
    switchMap,
    take,
    tap
} from 'rxjs/operators';
import { AppConfigQuery } from '../../core/state/app-config/app-config.query';
import { DropboxQuery } from './dropbox.query';
import { PrivateAssetQuery } from '../../pages/authenticated/private-asset/state/private-asset.query';
import GetThumbnailBatchResultEntry = files.GetThumbnailBatchResultEntry;
import ListFolderResult = files.ListFolderResult;
import { snapshotManager } from '@datorama/akita';
import GetThumbnailBatchResultEntrySuccess = files.GetThumbnailBatchResultEntrySuccess;
import { from, fromEvent, Observable, of, Subject } from 'rxjs';
import { SessionQuery } from '../../core/state/session/session.query';
import { LocationStrategy } from '@angular/common';
import { ToastService } from '../services/toast/toast.service';

@Injectable({ providedIn: 'root' })
export class DropboxService {
    dbx: Dropbox | undefined = undefined;
    dbxAuth?: DropboxAuth;
    REDIRECT_URI = `${location.origin}${this.locationStrategy.getBaseHref()}dropbox`;

    constructor(
        private dropboxStore: DropboxStore,
        private config: AppConfigQuery,
        private dropboxQuery: DropboxQuery,
        private privateAssetQuery: PrivateAssetQuery,
        private sessionQuery: SessionQuery,
        private locationStrategy: LocationStrategy,
        private toast: ToastService
    ) {
        this.dbxAuth = new DropboxAuth({
            clientId: this.config.get().dropboxClientId
        });

        this.dbx = new Dropbox({
            auth: this.dbxAuth
        });

        // sync dropbox state store across tabs and windows
        fromEvent<StorageEvent>(window, 'storage')
            .pipe(
                filter((event) => event.key === 'AkitaStores'),
                map((event) => JSON.parse(event.newValue)),
                filter((parsed) => parsed?.dropbox),
                map((parsed: { dropbox: DropboxState }) => ({
                    dropbox: parsed?.dropbox
                }))
            )
            .subscribe((state) => {
                const dropbox = {
                    ...snapshotManager.getStoresSnapshot(['dropbox'])['dropbox'],
                    ...state.dropbox
                };
                snapshotManager.setStoresSnapshot(
                    { dropbox },
                    {
                        skipStorageUpdate: true
                    }
                );
            });
    }

    /**
     * Connect - Connect to dropbox
     * Check access and sync files for passed path
     */
    connect(path: string = this.dropboxQuery.basePath) {
        return this.dropboxQuery.tokens$.pipe(
            filter((tokens) => !!tokens.refreshToken && !!tokens.accessToken),
            distinctUntilChanged(
                (p, q) =>
                    p.accessToken === q.accessToken && p.refreshToken === q.refreshToken
            ),
            tap((tokens) => this.dbxAuth.setRefreshToken(tokens.refreshToken)),
            tap((tokens) => this.dbxAuth.setAccessToken(tokens.accessToken)),
            switchMap((_) => this.checkAccess()),
            switchMap((_) =>
                this.sync(path).pipe(
                    catchError((err: DropboxResponseError<files.ListFolderError>) => {
                        // if folder not found...
                        if (err.status === 409) {
                            // ...create it...
                            path = path.substring(0, path.length - 1);
                            return from(this.dbx.filesCreateFolderV2({ path })).pipe(
                                // ...and sync again
                                switchMap((_) => this.sync(path))
                            );
                        } else {
                            this.toast.show({
                                intent: 'critical',
                                message: `Dropbox responded with error code: ${err.status}`,
                                title: 'Error syncing with Dropbox'
                            });
                            this.dropboxStore.setError(err);
                            return of(err);
                        }
                    })
                )
            )
        );
    }

    /**
     * Get thumbnail images for image files as base64 encoded strings
     *
     * @param resources
     */
    /*getThumbnails(resources: DropboxResource[]) {
        // Get thumbnails
        const images = ['jpg', 'jpeg', 'png', 'tiff', 'tif', 'gif', 'webp', 'ppm', 'bmp'];
        const entries: Array<ThumbnailArg> = resources
            .filter(
                (entry) =>
                    entry.tag === 'file' &&
                    images.some((fileEnding) => entry.name.endsWith(fileEnding))
            )
            .map((resource) => ({
                path: resource['path_lower'],
                size: { '.tag': 'w64h64' }
            }));

        // Make in chunks of 25 (max-size per batch)
        const maxSizePerBatch: number = 25;
        const chunk = (array, size) =>
            Array.from({ length: Math.ceil(array.length / size) }, (value, index) =>
                array.slice(index * size, index * size + size)
            );
        const chunked = chunk(entries, maxSizePerBatch);
        for (const batchEntires of chunked) {
            this.dbx
                .filesGetThumbnailBatch({ entries: batchEntires })
                .then((response) => this.setThumbnails(response.result.entries))
                .catch((error) => console.log(error));
        }
    }*/

    /**
     * Set thumbnail images in dropbox store
     *
     * @param entries
     */
    setThumbnails(entries: Array<GetThumbnailBatchResultEntry>) {
        /*console.log(
            entries,
            entries
                .filter((entry) => entry['.tag'] === 'success')
                .map((entry: GetThumbnailBatchResultEntrySuccess) => entry.metadata?.id)
        );*/
        const newState = entries
            .filter((entry) => entry['.tag'] === 'success')
            .map((entry: GetThumbnailBatchResultEntrySuccess) => ({
                id: entry.metadata?.id,
                metadata: entry.metadata,
                thumbnail: entry.thumbnail
            }));

        //this.dropboxStore.upsertMany(newState);
        //this.dropboxStore.update(ids, (state) => ({ ...state }));
        const thumbnails = {};
        entries.forEach((entry) => {
            if (entry['.tag'] == 'success') {
                thumbnails[entry.metadata?.id] = entry.thumbnail;
            }
        });
        this.dropboxStore.update((state) => ({
            ...state,
            thumbnails: { ...thumbnails }
        }));
    }

    /**
     * Upload a file to dropbox
     * @param dropboxFolder
     * @param dropboxfileName
     * @param contents
     */
    upload(
        dropboxFolder: string,
        dropboxfileName: string,
        contents: Object
    ): Promise<DropboxResponse<files.FileMetadata>> {
        return this.dbx.filesUpload({
            path: `${dropboxFolder}/${dropboxfileName}`,
            mode: { '.tag': 'overwrite' },
            contents: contents
        });
    }

    /**
     * Get link - Get temporary link for file
     * @param { string } path - path to file in dropbox
     * @returns { Observable<string> } - temporary link to file in dropbox
     */
    getLink$(path: string): Observable<string> {
        return from(this.dbx.filesGetTemporaryLink({ path })).pipe(
            pluck('result', 'link')
        );
    }

    /**
     * Authenticate - Authenticate against dropbox
     * Opens a dropbox authentication flow in new tab and asks for user permissions,
     * once accepted user is redirected back to our app and state is synced across tabs.
     * User can close the newly opened window and resume the session in the initial tab.
     */
    authenticate() {
        this.dropboxStore.update((state) => ({ ...state, connectState: 'loading' }));
        this.sessionQuery.userName$
            .pipe(
                switchMap((userName) =>
                    from(
                        this.dbxAuth.getAuthenticationUrl(
                            this.REDIRECT_URI,
                            userName,
                            'code',
                            'offline',
                            undefined,
                            undefined,
                            true
                        )
                    )
                ),
                take(1)
            )
            .subscribe(
                (authUrl) => {
                    window.sessionStorage.setItem(
                        'codeVerifier',
                        this.dbxAuth.getCodeVerifier()
                    );
                    window.open(<string>authUrl, '_blank');
                },
                (error) => {
                    console.error(error);
                }
            );
    }

    /**
     * Set code - Get access token from code with code verifier
     * @param { string } code - code received from dropbox
     * @returns { Observable } - observable with success or error message for authentication
     */
    setCode(code: string) {
        this.dbxAuth.setCodeVerifier(window.sessionStorage.getItem('codeVerifier'));
        return from(this.dbxAuth.getAccessTokenFromCode(this.REDIRECT_URI, code)).pipe(
            pluck('result'),
            tap((result: any) =>
                this.dropboxStore.update((state) => ({
                    ...state,
                    refreshToken: result.refresh_token,
                    accessToken: result.access_token,
                    connectState: 'success'
                }))
            ),
            map((res) => ({
                type: 'success',
                title: 'Success! 🎉',
                message:
                    'Your Swip Account is now connected to Dropbox.<br />You can close this window and return to Swip.',
                button: 'Close and return to Swip'
            })),
            catchError((err) => {
                // TODO: log error to backend err.error
                console.error(err.error);
                this.dropboxStore.update((state) => ({
                    ...state,
                    connectState: 'error'
                }));
                return of({
                    type: 'error',
                    title: 'Something went wrong! 😞',
                    message:
                        'We were unable to connect your Swip Account to Dropbox.<br />Please close this window and try again.',
                    button: 'Close and return to Swip'
                });
            })
        );
    }

    /**
     * Sync - Sync dropbox files with store
     * @param { string } path - dropbox path to sync files for.
     * @returns { Observable } - observable with dropbox entries matching path
     */
    sync(path: string = '') {
        // if root folder...
        if (path === '/') {
            // ...set path to empty
            path = '';
        }
        const request$: Subject<any> = new Subject();
        const continue$ = (cursor, entries) =>
            from(this.dbx.filesListFolderContinue({ cursor })).pipe(
                pluck('result'),
                scan(
                    (acc, value) => ({
                        ...value,
                        entries: [...acc.entries, ...value.entries]
                    }),
                    <Partial<ListFolderResult>>{ entries }
                ),
                switchMap((result) =>
                    result.has_more
                        ? continue$(result.cursor, result.entries)
                        : of(result)
                )
            );

        return request$.pipe(
            startWith(null),
            switchMap((cursor) =>
                cursor
                    ? this.dbx.filesListFolderLongpoll({
                          cursor,
                          timeout: 60 * 5
                      })
                    : of(true)
            ),
            switchMap((_) => this.dbx.filesListFolder({ path, recursive: true })),
            pluck('result'),
            switchMap((result) =>
                result.has_more ? continue$(result.cursor, result.entries) : of(result)
            ),
            tap((result: ListFolderResult) => request$.next(result.cursor)),
            pluck('entries'),
            map((entries) =>
                entries
                    .filter(
                        (entry) => entry['.tag'] === 'file' && entry.name !== 'meta.json'
                    )
                    .map(
                        (entry) =>
                            ({
                                ...entry,
                                folder: entry.path_lower.substring(
                                    0,
                                    entry.path_lower.lastIndexOf('/')
                                ),
                                type:
                                    entry.name.indexOf('.') !== -1
                                        ? entry.name.substring(
                                              entry.name.lastIndexOf('.'),
                                              entry.name.length
                                          )
                                        : 'n/a',
                                file_name:
                                    entry.name.substring(
                                        0,
                                        entry.name.lastIndexOf('.')
                                    ) || entry.name
                            } as DropboxResource)
                    )
            ),
            tap((entries) => this.dropboxStore.set(entries)),
            repeat(),
            share()
        );
    }

    /**
     * Check access - Check if user has access to dropbox
     * @returns { Observable } - observable echoed response on success
     */
    checkAccess() {
        return from(
            this.dbx.checkUser({
                query: 'access_allowed'
            })
        ).pipe(
            catchError((err) => {
                this.dropboxStore.reset();
                this.toast
                    .show({
                        intent: 'critical',
                        message: `Please connect to Dropbox again.`,
                        title: 'Dropbox could not be synced!',
                        actions: {
                            primary: 'Connect'
                        }
                    })
                    .$action.subscribe((_) => this.authenticate());
                return of(err);
            })
        );
    }

    /**
     * Revoke access - Revoke user access to dropbox (user still needs to remove app from dropbox settings)
     * @returns { Observable } - observable
     */
    revokeAccess() {
        return from(this.dbx.authTokenRevoke()).pipe(
            tap((_) => this.dropboxStore.reset())
        );
    }
}
